import { useCallback, useEffect, useRef, useState } from 'react';
import { useImageUrl } from '../api/useImageUrl';
import { CameraConnection, CameraPicker, ZoneEditor, addSafetyCamera } from '../components/liveCameras';

/**
 * Cameras and their zones — where the safety rules live.
 *
 * A rule only means something somewhere: a vest is required in the cell, not the
 * corridor; a doorway is no-entry, the aisle is not. So a zone is a rectangle on
 * a camera's view with a rule attached, and detection evaluates live frames
 * against them. The frame to draw on is the camera's reference still — a grab
 * from the feed, or an upload while sources are still being decided.
 */

interface Camera {
  id: number;
  name: string;
  source: string;
  location: string | null;
  referenceKey: string | null;
  zoneCount: number;
  customerId: number | null;
  customerName: string | null;
  /** Which detectors run on this feed. */
  kinds: string[];
  /** Face-blur: null inherits the customer default, true/false forces it. */
  privacyBlur: boolean | null;
}

/** The detectors a camera can run, in display order. */
const DETECTORS: Array<{ key: string; label: string }> = [
  { key: 'person', label: 'Person' },
  { key: 'ppe', label: 'PPE (helm / hesje)' },
  { key: 'fire', label: 'Fire / smoke' },
  { key: 'forklift', label: 'Forklift' },
];
interface CustomerOption {
  id: number;
  name: string;
}
const FIELD: React.CSSProperties = { display: 'grid', gap: 4, fontSize: 13, margin: 0 };

export function SafetyZonesView() {
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    fetch('/api/safety/cameras')
      .then((r) => (r.ok ? r.json() : []))
      .then((cs: Camera[]) => {
        setCameras(cs);
        setSelected((s) => s ?? cs[0]?.id ?? null);
      })
      .catch(() => undefined);
  }, []);
  useEffect(() => load(), [load]);

  useEffect(() => {
    fetch('/api/customers')
      .then((r) => (r.ok ? r.json() : []))
      .then((cs: CustomerOption[]) => setCustomers(cs))
      .catch(() => undefined);
  }, []);

  const addCamera = async () => {
    try {
      const id = await addSafetyCamera();
      if (id == null) return;
      setSelected(id);
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const reassign = async (cameraId: number, customerId: number | null) => {
    try {
      const res = await fetch(`/api/safety/cameras/${cameraId}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ customerId }),
      });
      if (!res.ok) throw new Error('Could not reassign camera');
      setNotice('Camera reassigned.');
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const setDetectors = async (cameraId: number, kinds: string[]) => {
    if (kinds.length === 0) {
      setError('A camera must run at least one detector.');
      return;
    }
    try {
      const res = await fetch(`/api/safety/cameras/${cameraId}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ kinds }),
      });
      if (!res.ok) throw new Error('Could not update detectors');
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // Face-blur override for one camera: null = inherit the customer default.
  const setPrivacyBlur = async (cameraId: number, privacyBlur: boolean | null) => {
    try {
      const res = await fetch(`/api/safety/cameras/${cameraId}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ privacyBlur }),
      });
      if (!res.ok) throw new Error('Could not update privacy setting');
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  };


  const camera = cameras.find((c) => c.id === selected) ?? null;
  // The still the zones are drawn on — the stored reference. Uploading a new one
  // is handled here and handed to the shared editor as its "replace" action.
  const referenceUrl = useImageUrl(camera?.referenceKey ?? '');
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadReference = async (file: File) => {
    if (!camera) return;
    try {
      const form = new FormData();
      form.append('image', file, file.name);
      const res = await fetch(`/api/safety/cameras/${camera.id}/reference`, { method: 'POST', body: form });
      if (!res.ok) throw new Error('Upload failed');
      setNotice('Reference frame set.');
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div>
      <h1 className="view-title">Safety cameras &amp; zones</h1>
      <p className="muted" style={{ marginTop: -8, maxWidth: 720 }}>
        A zone is a free-form area you outline on a camera's view, with a rule attached — no-entry
        (any person), PPE required, all-humans, no-vehicles, or an occupancy cap. Detection
        evaluates the live feed against them and raises an event the moment a rule is broken.
      </p>

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 16 }}>{error}</div>}
      {notice && <div className="panel" style={{ color: 'var(--good)', marginBottom: 16 }}>{notice}</div>}

      <SiteAlarmCard />

      {/* Pick a camera, or add one — a dropdown so it scales past a handful. */}
      <div className="row" style={{ marginBottom: 16, gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="muted" style={{ fontSize: 13 }}>Camera</span>
        <CameraPicker cameras={cameras} value={selected} onChange={setSelected} onAdd={() => void addCamera()} style={{ minWidth: 280 }} />
        <button className="btn sm" onClick={() => void addCamera()}>＋ Add camera</button>
      </div>

      {camera && (
        <div className="panel" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
            1 · Connect the camera <span className="muted" style={{ fontWeight: 400 }}>— its IP and login, then grab a still to draw on</span>
          </div>
          <CameraConnection camera={camera} onChanged={load} onNotice={setNotice} onError={setError} />

          <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '16px 0' }} />

          <div className="row" style={{ gap: 24, flexWrap: 'wrap', alignItems: 'start' }}>
            <label style={{ ...FIELD }}>
              Belongs to
              <select
                value={camera.customerId ?? ''}
                onChange={(e) => void reassign(camera.id, e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Unassigned (staff-only)</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>
            <div>
              <div className="muted" style={{ fontSize: 13, marginBottom: 6 }}>Watch for</div>
              <div className="row" style={{ gap: 14, flexWrap: 'wrap' }}>
                {DETECTORS.map((d) => {
                  const on = camera.kinds.includes(d.key);
                  return (
                    <label key={d.key} className="row" style={{ gap: 6, margin: 0, cursor: 'pointer', fontSize: 14 }}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() =>
                          void setDetectors(camera.id, on ? camera.kinds.filter((k) => k !== d.key) : [...camera.kinds, d.key])
                        }
                      />
                      {d.label}
                    </label>
                  );
                })}
              </div>
            </div>
            <label style={{ ...FIELD }}>
              Blur faces
              <select
                value={camera.privacyBlur === null ? 'inherit' : camera.privacyBlur ? 'on' : 'off'}
                onChange={(e) => void setPrivacyBlur(camera.id, e.target.value === 'inherit' ? null : e.target.value === 'on')}
              >
                <option value="inherit">Inherit customer</option>
                <option value="on">On</option>
                <option value="off">Off</option>
              </select>
            </label>
          </div>
        </div>
      )}

      {camera && (
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
          2 · Draw the zones <span className="muted" style={{ fontWeight: 400 }}>— outline an area, then give it a rule</span>
        </div>
      )}

      {camera ? (
        <>
          <ZoneEditor
            cameraId={camera.id}
            imageUrl={referenceUrl ?? ''}
            onSaved={(n) => {
              setNotice(`${n} zone${n === 1 ? '' : 's'} saved.`);
              load();
            }}
            onError={setError}
            replace={{
              label: camera.referenceKey ? 'Replace reference' : 'Upload reference',
              onClick: () => fileRef.current?.click(),
            }}
          />
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void uploadReference(f);
              e.target.value = '';
            }}
          />
        </>
      ) : (
        <div className="panel muted">No cameras yet. Add one to start.</div>
      )}
    </div>
  );
}
interface AlarmTarget {
  name: string;
  url: string;
  method: string;
  severities: string[];
  enabled: boolean;
}

/**
 * The site's physical alarms — one or more relays/sirens/lamps/bells reached by a
 * webhook, each firing on its own severities. So a beacon can flash on every event
 * while a siren sounds only on critical. GET suits URL-triggered relays (a Shelly);
 * POST sends the event JSON (Home Assistant, Node-RED, chat).
 */
function SiteAlarmCard() {
  const [targets, setTargets] = useState<AlarmTarget[] | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/safety/alarm-config')
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { targets?: AlarmTarget[] } | null) => setTargets(d?.targets ?? []))
      .catch(() => setTargets([]));
  }, []);
  if (!targets) return null;

  const update = (i: number, patch: Partial<AlarmTarget>) => setTargets((ts) => ts!.map((t, k) => (k === i ? { ...t, ...patch } : t)));
  const toggleSev = (i: number, s: string) => {
    const cur = targets[i]!.severities;
    update(i, { severities: cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s] });
  };
  const add = () =>
    setTargets((ts) => [
      ...ts!,
      ts!.length === 0
        ? { name: 'Beacon', url: '', method: 'GET', severities: ['critical', 'warning'], enabled: true }
        : { name: 'Siren', url: '', method: 'GET', severities: ['critical'], enabled: true },
    ]);
  const remove = (i: number) => setTargets((ts) => ts!.filter((_, k) => k !== i));

  const save = async () => {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const res = await fetch('/api/safety/alarm-config', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ targets }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Save failed');
      setMsg('Saved.');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const test = async (i: number) => {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const res = await fetch('/api/safety/alarm-config/test', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ index: i }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.message || 'Test failed');
      setMsg(`"${targets[i]!.name}" fired${j.status ? ` (HTTP ${j.status})` : ''} — check it.`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const active = targets.filter((t) => t.enabled && t.url).length;

  return (
    <div className="panel" style={{ marginBottom: 16, borderLeft: `3px solid ${active ? 'var(--good)' : 'var(--border)'}` }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setOpen((v) => !v)}>
        <strong style={{ fontSize: 13 }}>
          Physical alarm on site{' '}
          <span className="muted" style={{ fontWeight: 400 }}>
            — {active ? `${active} active (relay / siren / lamp)` : 'off'}
          </span>
        </strong>
        <span className="muted" style={{ fontSize: 12 }}>{open ? 'Hide ▲' : 'Configure ▾'}</span>
      </div>

      {open && (
        <div className="grid" style={{ gap: 12, marginTop: 12 }}>
          {targets.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No alarms yet — add one below.</div>}
          {targets.map((t, i) => (
            <div key={i} className="panel" style={{ padding: 12, background: 'var(--panel-2)' }}>
              <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="checkbox" checked={t.enabled} onChange={(e) => update(i, { enabled: e.target.checked })} title="Enabled" />
                <input value={t.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="Beacon" style={{ flex: '1 1 120px', fontWeight: 600 }} />
                <select value={t.method} onChange={(e) => update(i, { method: e.target.value })} style={{ flex: '0 0 130px' }}>
                  <option value="GET">GET (relay)</option>
                  <option value="POST">POST (JSON)</option>
                </select>
                <button className="btn sm" disabled={busy || !t.url} onClick={() => void test(i)} title="Fire this one now">Test</button>
                <button className="btn sm" onClick={() => remove(i)} title="Remove">✕</button>
              </div>
              <input
                value={t.url}
                onChange={(e) => update(i, { url: e.target.value })}
                placeholder="http://192.168.1.50/rpc/Switch.Set?id=0&on=true&toggle_after=30"
                style={{ width: '100%', marginTop: 8, fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }}
              />
              <div className="row" style={{ gap: 14, fontSize: 13, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                <span className="muted">Fire on:</span>
                {(['critical', 'warning', 'info'] as const).map((s) => (
                  <label key={s} className="row" style={{ gap: 6, alignItems: 'center' }}>
                    <input type="checkbox" checked={t.severities.includes(s)} onChange={() => toggleSev(i, s)} /> {s}
                  </label>
                ))}
              </div>
            </div>
          ))}

          <div className="row" style={{ gap: 8 }}>
            <button className="btn sm" onClick={add}>＋ Add alarm</button>
            <button className="btn sm primary" disabled={busy} onClick={() => void save()}>Save</button>
          </div>
          {msg && <div style={{ color: 'var(--good)', fontSize: 12 }}>{msg}</div>}
          {err && <div style={{ color: 'var(--bad)', fontSize: 12 }}>{err}</div>}
          <div className="muted" style={{ fontSize: 11, lineHeight: 1.5 }}>
            Example — a beacon on all events and a siren only on critical: two targets, both <b>GET</b>, each a Shelly URL like{' '}
            <code>http://&lt;ip&gt;/rpc/Switch.Set?id=0&amp;on=true&amp;toggle_after=30</code>. Give each Shelly a fixed IP on the same
            network as the server. A camera's own speaker usually needs its vendor app, so a plug-in siren + a Shelly is the reliable route.
          </div>
        </div>
      )}
    </div>
  );
}
